import { Link } from '@inertiajs/react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export interface ApplicationRow {
  id: string
  name: string
  app_id: string | null
  active: boolean
  created_at: string
}

const columnHelper = createColumnHelper<ApplicationRow>()

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: (info) => <span className="font-medium">{info.getValue()}</span>,
  }),
  columnHelper.accessor('app_id', {
    header: 'App ID',
    cell: (info) => {
      const value = info.getValue()
      return value ? (
        <span className="font-mono text-xs text-muted-foreground">
          {value.slice(0, 16)}&hellip;
        </span>
      ) : (
        <span className="text-muted-foreground text-xs">—</span>
      )
    },
  }),
  columnHelper.accessor('active', {
    header: 'Status',
    cell: (info) => (
      <Badge variant={info.getValue() ? 'default' : 'secondary'}>
        {info.getValue() ? 'active' : 'inactive'}
      </Badge>
    ),
  }),
  columnHelper.accessor('created_at', {
    header: 'Created',
    cell: (info) => (
      <span className="text-muted-foreground text-sm">
        {new Date(info.getValue()).toLocaleDateString()}
      </span>
    ),
  }),
  columnHelper.accessor('id', {
    header: '',
    cell: (info) => (
      <Button variant="ghost" size="sm" asChild>
        <Link href={`/dashboard/applications/${info.getValue()}`}>View</Link>
      </Button>
    ),
  }),
]

interface ApplicationsTableProps {
  data: ApplicationRow[]
}

export function ApplicationsTable({ data }: ApplicationsTableProps) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="text-center text-muted-foreground py-8">
                No applications yet.
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}
